import { mapGetters, mapMutations, mapActions } from "vuex";
import {
  priorities,
  dataSourceTypes,
  authenticationURLList,
  localStorageKeys,
  clientRoles,
  amplitudeEventNames
} from "@/enums";

const googleAnalyticsDataSource = {
  computed: {
    ...mapGetters('common', [ 'getDataSources' ]),
    googleAnalyticsDataSource() {
      return this.getDataSources.find(dataSource => dataSource.sourceType == dataSourceTypes.googleAnalytics);
    },
  }
}

const googleAnalyticsCurrency = {
  mixins: [ googleAnalyticsDataSource ],
  computed: {
    googleAnalyticsCurrency() {
      if (this.googleAnalyticsDataSource) {
        return this.googleAnalyticsDataSource.currency
      }
      return "";
    },
  },
}

const generateLoaderStatus = {
  methods: {
    generateLoaderStatus(name, status, text) {
      return { name: name, status: status, text: text };
    }
  }
}

const unConnectedIntegrations = {
  computed: {
    ...mapGetters('common', [ 'getIntegrationsStatus' ]),
    unConnectedIntegrations() {
      return Object.keys(this.getIntegrationsStatus).filter((key) => !this.getIntegrationsStatus[key])
        .map(integration => dataSourceTypes[integration])
    }
  }
}


const checkDataSources = {
  computed: {
    ...mapGetters('common', [ 'getApisLoadStates' ]),
  },
  methods: {
    ...mapActions('common', [ 'setDataSources' ]),
    ...mapMutations('common', [ 'setApisLoadStates' ]),
    async checkDataSources() {
      if (this.getApisLoadStates.dataSources?.isCalled) return;

      this.setApisLoadStates({ api: 'dataSources', isCalled: true });
      await this.setDataSources();
    },
  }
}

const changePopupStatus = {
  methods: {
    ...mapMutations('common', [ 'setPopupData' ]),

    changePopupStatus(popupName, isPopupOpen, data) {
      this.setPopupData({
        popupName: popupName,
        isPopupOpen: isPopupOpen,
        data: data
      });
    }
  }
}

const changeLoaderStatus = {
  methods: {
    ...mapMutations('common', [ 'setLoaderStatus' ]),
    changeLoaderStatus(name, status, text) {
      this.setLoaderStatus({
        name: name,
        status: status,
        text: text
      });
    }
  }
}

const appSumoHelpCrunchCheck = {
  methods: {
    appSumoHelpCrunchCheck(errorMessage, eventName) {
      if (errorMessage.includes('Contact us')) {
        HelpCrunch('openChat')
        HelpCrunch('trackEvent', 'contact_us_tier_3_' + eventName)

        return false
      }

      return true
    }
  }
}

const warningMessages = {
  data() {
    return {
      warningMessages: [],
      defaultWarningType: priorities.urgent.toLowerCase(),
    }
  },
  computed: {
    hasWarning() {
      return this.warningMessages.length;
    },
  },
  methods: {
    addWarning(warning, type) {
      if (this.warningMessages.find(warningMessages => warningMessages.warning == warning)) return;

      this.warningMessages.push({
        type: type || this.defaultWarningType,
        warning: warning
      })
    },
    removeWarnings() {
      this.warningMessages = []
    },
    removeWarning(warning) {
      this.warningMessages = this.warningMessages.filter(warningMessage => warningMessage.warning != warning);
    }
  }
}


const addAmplitudeLogEvent = {
  methods: {
    addAmplitudeLogEvent(eventName, eventProperties) {
      amplitude.getInstance().logEvent(eventName, (eventProperties || {}));
    }
  }
}

const hideOnClickOutside = {
  data() {
    return {
      outsideClickEventElements: {
        menuOpenStatus: false,
      }
    }
  },
  mounted() {
    document.addEventListener("click", this.hideOnClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.hideOnClickOutside);
  },
  methods: {
    hideOnClickOutside(event) {
      if (!this.outsideClickEventElements.ref || this.outsideClickEventElements.ref.contains(event.target)) return;

      this.outsideClickEventElements.menuOpenStatus = false;
    },
  }
}

const sendUserInformationToAmplitude = {
  mixins: [ addAmplitudeLogEvent ],
  data() {
    return {
      sessionMessages: {
        register: 'Register Success',
        login: 'Success'
      }
    }
  },
  computed: {
    ...mapGetters('auth', [ 'getUserInformation' ]),
    ...mapGetters('common', [
      'getActiveClientID',
      'getActiveClient',
      'getLocalStorageValueByKey',
      'getApisLoadStates'
    ]),
    activeClientName() {
      return this.getActiveClient.name
    },
  },
  methods: {
    ...mapMutations('common', [ 'changeGeneralLocalStorageVariables' ]),
    sendUserInformationToAmplitude() {

      const identify = new amplitude.Identify()
        .set('plan', this.getUserInformation.plan)
        .set('appsumo', this.getUserInformation.isAppsumoUser)
        .set('invited_user', this.getUserInformation.isInvitedUser);

      amplitude.getInstance().identify(identify);

      if (this.getUserInformation.isOnboardingCompleted) {

        this.addAmplitudeLogEvent(amplitudeEventNames.viewClient, {
          client_name: this.activeClientName
        });

      }

      if (this.getLocalStorageValueByKey(localStorageKeys.newSession)) {

        this.addAmplitudeLogEvent(this.getLocalStorageValueByKey(localStorageKeys.newSession), {
          title: this.sessionMessages[this.getLocalStorageValueByKey(localStorageKeys.newSession)],
        });
        this.removeSessionKey();

      }

    },
    removeSessionKey() {
      this.changeGeneralLocalStorageVariables({
        removeKey: true,
        key: localStorageKeys.newSession
      })
    }
  }
}


const getIntegrationURL = {
  computed: {
    ...mapGetters("auth", [ "getUserInformation" ]),
    ...mapGetters("common", [ "getApiBaseUrl" ]),
    isOnboardingCompletedURLQuery() {
      return `isOnboardingCompleted=${this.getUserInformation.isOnboardingCompleted}`;
    },
  },
  methods: {
    getIntegrationURL(type){
      return `${this.getApiBaseUrl}${authenticationURLList[type]}?${this.isOnboardingCompletedURLQuery}`
    }
  }
};


const setActiveClientID = {
  computed: {
    ...mapGetters("common", [ "getClients" ])
  },
  methods: {
    ...mapMutations("common", [ "changeGeneralLocalStorageVariables" ]),
    setActiveClientID(clientID = null) {
      let activeClientID;

      if (clientID) {
        activeClientID = clientID;
      } else if (this.getClients.length) {
        activeClientID = this.getClients[0]._id;
      }

      if (activeClientID) {
        this.changeGeneralLocalStorageVariables({
          key: localStorageKeys.activeClientID,
          value: activeClientID
        });
      }
    }
  }
};

const setActiveUserRole = {
  computed: {
    ...mapGetters("auth", [ "getUserInformation" ]),
    ...mapGetters("common", [ "getActiveClient" ])
  },
  methods: {
    ...mapMutations("common", [ "changeGeneralLocalStorageVariables" ]),
    setActiveUserRole() {
      if (!this.getUserInformation.email) return;

      const authedUser = this.getActiveClient.authedUser;
      let userRole = "";

      if (this.getActiveClient.email && this.getActiveClient.email == this.getUserInformation.email) {
        userRole = clientRoles.owner;
      } else {

        if (!authedUser || !authedUser.length) return;

        const [ _, activeUser ] = Object.entries(authedUser).find(([ _, user ]) => user.user == this.getUserInformation.email);

        if (activeUser) {
          userRole = activeUser.role;
        }
      }


      if(userRole){
        this.changeGeneralLocalStorageVariables({
          key: localStorageKeys.userRole,
          value: userRole
        });
      }
    }
  }
};

export {
  googleAnalyticsCurrency,
  googleAnalyticsDataSource,
  generateLoaderStatus,
  unConnectedIntegrations,
  checkDataSources,
  changePopupStatus,
  appSumoHelpCrunchCheck,
  warningMessages,
  changeLoaderStatus,
  addAmplitudeLogEvent,
  hideOnClickOutside,
  sendUserInformationToAmplitude,
  getIntegrationURL,
  setActiveClientID,
  setActiveUserRole
}




// WEBPACK FOOTER //
// ./src/mixins/common/index.js